<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {computed, ref, watch} from "vue";
import {useBoardingStore} from "@/stores/Boarding/Boarding.js";
import BoardingFilter from "@/views/Boarding/Components/BoardingFilter.vue";
import axios from "axios";

export default {
    props: {
        category: String,
    },
    components: {BoardingFilter},
    setup(props) {
        const {
            loading,
            total,
            itemsPerPage,
            filter,
        } = useSharedData();

        const {getStatusColor, transformFilterValues, formatedDate} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const boardingStore = useBoardingStore();
        const securityStore = useSecurityStore();

        const title = computed(() => {
            if (props.category === 'ON') {
                return $i18n.t('module.on_boarding');
            } else if (props.category === 'OFF') {
                return $i18n.t('module.off_boarding');
            }
            return '';
        });

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "0%",
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                value: "code",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.worker"),
                align: "left",
                value: "workerName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.process_model"),
                align: "left",
                value: "processModelName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.effective_date"),
                align: "left",
                value: "effectiveDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.planned_start_date"),
                align: "left",
                value: "plannedStartDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.planned_end_date"),
                align: "left",
                value: "plannedEndDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.actual_start_date"),
                align: "left",
                value: "actualStartDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.actual_end_date"),
                align: "left",
                value: "actualEndDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.status"),
                align: "left",
                value: "status",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.advancement"),
                align: "left",
                value: "achievedTasks",
                sortable: true,
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_BOARDING'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.edit`, params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['DELETE_BOARDING'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.delete`, params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.documents`, params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("module.template_application"),
                icon: 'mdi-text-box-multiple',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${rootPath.value}.template_applications`, params: { id: item.id }})
                },
            },
        ];

        const searchParams = ref({
            sortBy: "effectiveDate",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage,
        });


        function handleSearch(params) {
            loading.value = true;
            boardingStore.setBoardings([]);
            total.value = 0;

            filter.value.category = props.category;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/boardings', {params: transformFilterValues(filter.value)}).then((response) => {
                boardingStore.setBoardings(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});


        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        function view(item) {
            $router.push({name: `${rootPath.value}.view`, params: {id: item.id}});
        }

        const rootPath = computed(() => {
            if (props.category === 'ON') {
                return 'on_boardings';
            } else if (props.category === 'OFF') {
                return 'off_boardings';
            }
            return '';
        });

        return {
            title,
            boardingStore,
            securityStore,
            searchParams,
            headers,
            actions,
            loading,
            total,
            filter,
            rootPath,
            clearFilter,
            findAll,
            getStatusColor,
            formatedDate,
            view,
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="title"
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <boarding-filter v-model="filter" :title="title" :category="category"/>
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
                <w-btn-add
                    :show="securityStore.authorities['SAVE_BOARDING']"
                    @press="$router.push({name: `${rootPath}.add`})"
                />
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="boardingStore.boardings"
                    :loading="loading"
                    :total="total"
                    :root-path="rootPath"
                    module="BOARDING"
                    @rowDbClick="view"
                >

                    <template v-slot:item.code="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:item.workerName="{ item }">
                        <w-inline-text bold :text="item.workerName"/>
                    </template>
                    <template v-slot:item.processModelName="{ item }">
                        <w-inline-text :text="item.processModelName"/>
                    </template>
                    <template v-slot:item.effectiveDate="{ item }">
                        <w-chip color="teal" :text="formatedDate(item.effectiveDate)"/>
                    </template>
                    <template v-slot:item.plannedStartDate="{ item }">
                        <w-inline-text :text="formatedDate(item.plannedStartDate)"/>
                    </template>
                    <template v-slot:item.plannedEndDate="{ item }">
                        <w-inline-text :text="formatedDate(item.plannedEndDate)"/>
                    </template>
                    <template v-slot:item.actualStartDate="{ item }">
                        <w-inline-text :text="formatedDate(item.actualStartDate)"/>
                    </template>
                    <template v-slot:item.actualEndDate="{ item }">
                        <w-inline-text outlined :text="formatedDate(item.actualEndDate)"/>
                    </template>
                    <template v-slot:item.status="{ item }">
                        <w-chip :color="getStatusColor(`BOARDING_${item.status}`)" :text="$t(`boarding.${item.status}`)"/>
                    </template>
                    <template v-slot:item.achievedTasks="{ item }">
                        <v-progress-linear
                            color="teal lighten-2"
                            buffer-value="0"
                            :value="(item.achievedTasks * 100 / item.tasksSize).toFixed(2)"
                            stream
                            height="25"
                        >
                            <strong>
                                {{ `${item.achievedTasks}/${item.tasksSize} (${(item.achievedTasks * 100 / item.tasksSize).toFixed(2)})` }}
                            </strong>
                        </v-progress-linear>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>